import client from 'client'
import type {
  StartAssetActionAssetAssetIdActionPostMutationRequest,
  StartAssetActionAssetAssetIdActionPostMutationResponse,
  StartAssetActionAssetAssetIdActionPostPathParams,
  StartAssetActionAssetAssetIdActionPostQueryParams,
  StartAssetActionAssetAssetIdActionPost422,
} from '../../types/StartAssetActionAssetAssetIdActionPost.ts'
import type { UseMutationOptions } from '@tanstack/react-query'
import type { RequestConfig, ResponseConfig, ResponseErrorConfig } from 'client'
import { useMutation } from '@tanstack/react-query'

export const startAssetActionAssetAssetIdActionPostMutationKey = () => [{ url: '/asset/{asset_id}/action' }] as const

export type StartAssetActionAssetAssetIdActionPostMutationKey = ReturnType<typeof startAssetActionAssetAssetIdActionPostMutationKey>

/**
 * @description Execute a typed action on an asset. The action type determines the required payload structure:        - **ASSIGN_TO_COMPANY**: Requires `company_id` field    - **SWITCH_RELAY**: Requires `new_relay_state` field (ON/OFF) - only for input/output modules    - **FIRMWARE_UPGRADE**: Optional `format_flag` and `debug_flag` boolean fields for firmware upgrade options    - **Configuration actions** (SYNC_CONFIG, FACTORY_RESET_2100, GET_CONFIG, etc.): No additional fields required
 * @summary Execute action on asset
 * {@link /asset/:asset_id/action}
 */
export async function startAssetActionAssetAssetIdActionPost(
  asset_id: StartAssetActionAssetAssetIdActionPostPathParams['asset_id'],
  data?: StartAssetActionAssetAssetIdActionPostMutationRequest,
  params?: StartAssetActionAssetAssetIdActionPostQueryParams,
  config: Partial<RequestConfig<StartAssetActionAssetAssetIdActionPostMutationRequest>> & { client?: typeof client } = {},
) {
  const { client: request = client, ...requestConfig } = config

  const res = await request<
    StartAssetActionAssetAssetIdActionPostMutationResponse,
    ResponseErrorConfig<StartAssetActionAssetAssetIdActionPost422>,
    StartAssetActionAssetAssetIdActionPostMutationRequest
  >({ method: 'POST', url: `/asset/${asset_id}/action`, params, data, ...requestConfig })
  return res
}

/**
 * @description Execute a typed action on an asset. The action type determines the required payload structure:        - **ASSIGN_TO_COMPANY**: Requires `company_id` field    - **SWITCH_RELAY**: Requires `new_relay_state` field (ON/OFF) - only for input/output modules    - **FIRMWARE_UPGRADE**: Optional `format_flag` and `debug_flag` boolean fields for firmware upgrade options    - **Configuration actions** (SYNC_CONFIG, FACTORY_RESET_2100, GET_CONFIG, etc.): No additional fields required
 * @summary Execute action on asset
 * {@link /asset/:asset_id/action}
 */
export function useStartAssetActionAssetAssetIdActionPost(
  options: {
    mutation?: UseMutationOptions<
      ResponseConfig<StartAssetActionAssetAssetIdActionPostMutationResponse>,
      ResponseErrorConfig<StartAssetActionAssetAssetIdActionPost422>,
      {
        asset_id: StartAssetActionAssetAssetIdActionPostPathParams['asset_id']
        data?: StartAssetActionAssetAssetIdActionPostMutationRequest
        params?: StartAssetActionAssetAssetIdActionPostQueryParams
      }
    >
    client?: Partial<RequestConfig<StartAssetActionAssetAssetIdActionPostMutationRequest>> & { client?: typeof client }
  } = {},
) {
  const { mutation: mutationOptions, client: config = {} } = options ?? {}
  const mutationKey = mutationOptions?.mutationKey ?? startAssetActionAssetAssetIdActionPostMutationKey()

  return useMutation<
    ResponseConfig<StartAssetActionAssetAssetIdActionPostMutationResponse>,
    ResponseErrorConfig<StartAssetActionAssetAssetIdActionPost422>,
    {
      asset_id: StartAssetActionAssetAssetIdActionPostPathParams['asset_id']
      data?: StartAssetActionAssetAssetIdActionPostMutationRequest
      params?: StartAssetActionAssetAssetIdActionPostQueryParams
    }
  >({
    mutationFn: async ({ asset_id, data, params }) => {
      return startAssetActionAssetAssetIdActionPost(asset_id, data, params, config)
    },
    mutationKey,
    ...mutationOptions,
  })
}